<script setup lang="ts">
// Destination groups: named sets of destinations (across encodes) started and
// stopped together. One chip per group with its sending count.
import { computed, ref } from 'vue'
import { api } from '../api'
import { useNode } from '../store'
import ConfirmDialog from './ConfirmDialog.vue'
import { scheduleNote } from '../schedule'

const emit = defineEmits<{ edit: [id?: string] }>()
const node = useNode()
const groups = computed(() => node.config?.groups ?? [])
const confirm = ref<{ id: string; name: string; action: 'start' | 'stop'; n: number } | null>(null)

function counts(ids: string[]) {
    const st = node.status!.destinations
    const on = ids.filter((d) => st[d]?.state === 'connected').length
    const bad = ids.filter((d) => st[d]?.state === 'error').length
    const enabled = ids.filter((d) => st[d] && st[d].state !== 'disabled').length
    return { on, bad, enabled, total: ids.length }
}
async function run() {
    const c = confirm.value!
    confirm.value = null
    try {
        const cfg = await (c.action === 'start'
            ? api.groupStart(c.id, node.config!.version)
            : api.groupStop(c.id, node.config!.version))
        node.config = cfg
        node.refreshPair()
    } catch (e) {
        node.fail(e)
    }
}
</script>

<template>
    <div class="flex min-h-10 flex-wrap items-center gap-2 border-b border-line chrome bg-bg-1 px-3 py-1.5">
        <span class="mr-1 text-[11px] font-semibold tracking-[.06em] text-fg-3 uppercase">Groups</span>
        <div
            v-for="g in groups"
            :key="g.id"
            class="flex h-7 items-center gap-1 rounded-md border bg-bg-2 pr-1 pl-1.5"
            :class="counts(g.destinations).bad ? 'border-bad/60' : 'border-line'"
        >
            <button
                class="btn-ghost px-1 py-px text-fg"
                :title="'Edit group'"
                :disabled="node.readOnly"
                @click="emit('edit', g.id)"
            >
                {{ g.name }}
            </button>
            <span class="font-mono text-xs text-fg-2 tabular-nums"
                >{{ counts(g.destinations).on }}/{{ counts(g.destinations).total }}</span
            >
            <span
                v-if="scheduleNote(g.schedule, new Date(node.now))"
                class="text-xs text-fg-2"
                :data-test="`group-schedule-${g.id}`"
                >{{ scheduleNote(g.schedule, new Date(node.now)) }}</span
            >
            <button
                v-if="!node.readOnly"
                class="btn-ghost px-1.75 py-px text-xs"
                :disabled="counts(g.destinations).enabled === g.destinations.length"
                @click="confirm = { id: g.id, name: g.name, action: 'start', n: g.destinations.length }"
            >
                start
            </button>
            <button
                v-if="!node.readOnly"
                class="btn-ghost px-1.75 py-px text-xs"
                :disabled="counts(g.destinations).enabled === 0"
                @click="confirm = { id: g.id, name: g.name, action: 'stop', n: g.destinations.length }"
            >
                stop
            </button>
        </div>
        <button v-if="!node.readOnly" class="btn-ghost py-1 text-xs" @click="emit('edit')">+ group</button>
        <span v-if="!groups.length" class="text-xs text-fg-3 max-[640px]:hidden"
            >none yet: a group starts and stops destinations together</span
        >
        <ConfirmDialog
            v-if="confirm"
            :title="`${confirm.action === 'start' ? 'Start' : 'Stop'} ${confirm.name}?`"
            :body="`${confirm.n} destination${confirm.n > 1 ? 's' : ''} will be ${confirm.action === 'start' ? 'enabled and connect' : 'disabled and stop sending'} at the same time.`"
            :confirm-label="confirm.action === 'start' ? 'Start all' : 'Stop all'"
            :danger="confirm.action === 'stop'"
            @confirm="run"
            @cancel="confirm = null"
        />
    </div>
</template>
